import { Pipe, PipeTransform } from '@angular/core';
import { Marked, Tokens } from 'marked';
import { escapeHtmlAttribute } from './ref-link.utils';

@Pipe({
  name: 'markdownRendering',
})
export class MarkdownRenderingPipe implements PipeTransform {
  private readonly marked = createMarked();

  transform(value?: string | null): string {
    if (!value) {
      return '';
    }
    return (this.marked.parse(value, { async: false }) as string).trim();
  }
}

// Quotes stay as they are: the pipes after this one parse the references and citation JSON, and
// the de-anonymizer undoes exactly this round (`decodeMarkupEscaping`).
const ESCAPE_REPLACEMENTS: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };

export function escapeMarkdownText(text: string): string {
  return text.replaceAll(/[&<>]/g, (char) => ESCAPE_REPLACEMENTS[char]);
}

/** Section titles sit inside 14px body copy, so every level stays close to it. */
const HEADING_CLASSES: readonly string[] = [
  'subtitle-1 text-black mt-4 mb-2 first:mt-0',
  'subtitle-1 text-black mt-4 mb-2 first:mt-0',
  'subtitle-2 text-black mt-3 mb-1 first:mt-0',
  'text-body-2 font-semibold text-black mt-2 mb-1 first:mt-0',
  'text-body-2 font-semibold text-neutral-90 mt-2 mb-1 first:mt-0',
  'text-body-2 font-semibold text-neutral-90 mt-2 mb-1 first:mt-0',
];

const CELL_ALIGN_CLASSES: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const LINK_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:']);

// Parsed rather than matched: the browser ignores control characters and spaces in front of the
// scheme, so a prefix check lets `\u0001javascript:` through.
function readLinkUrl(href: string): string | null {
  try {
    const url = new URL(href);
    return LINK_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function createMarked(): Marked {
  return new Marked({
    gfm: true,
    // AI answers use single newlines inside a paragraph as line breaks.
    breaks: true,
    tokenizer: {
      // No HTML blocks: a line holding only a tag would otherwise swallow the markdown after it.
      html: () => undefined,
      // No bare-URL autolinks: they would break the JSON of web-search citations.
      url: () => undefined,
    },
    renderer: {
      text(token) {
        if ('tokens' in token && token.tokens) {
          return this.parser.parseInline(token.tokens);
        }
        return escapeMarkdownText(token.text);
      },
      html({ text }) {
        return escapeMarkdownText(text);
      },
      heading({ tokens, depth }) {
        const level = Math.min(Math.max(depth, 1), 6);
        const classes = HEADING_CLASSES[level - 1];
        return `<h${level} class="${classes}">${this.parser.parseInline(tokens)}</h${level}>\n`;
      },
      paragraph({ tokens }) {
        return `<p class="my-2 first:mt-0 last:mb-0">${this.parser.parseInline(tokens)}</p>\n`;
      },
      hr() {
        return '<hr class="border-0 border-t border-neutral-20 my-3">\n';
      },
      blockquote({ tokens }) {
        return `<blockquote class="my-2 border-l-4 border-neutral-30 pl-3 text-neutral-70">\n${this.parser.parse(tokens)}</blockquote>\n`;
      },
      list(token) {
        const items = token.items.map((item) => this.listitem(item)).join('');
        if (token.ordered) {
          const start = token.start !== '' && token.start !== 1 ? ` start="${token.start}"` : '';
          return `<ol class="list-decimal pl-5 my-2 space-y-1"${start}>\n${items}</ol>\n`;
        }
        return `<ul class="list-disc pl-5 my-2 space-y-1">\n${items}</ul>\n`;
      },
      table(token) {
        const headerCells = token.header.map((cell) => this.tablecell(cell)).join('');
        const rows = token.rows
          .map((row) => `<tr>${row.map((cell) => this.tablecell(cell)).join('')}</tr>\n`)
          .join('');
        return (
          '<div class="overflow-x-auto my-3">\n' +
          '<table class="w-full border-collapse text-body-2">\n' +
          `<thead>\n<tr>${headerCells}</tr>\n</thead>\n` +
          `<tbody>\n${rows}</tbody>\n` +
          '</table>\n</div>\n'
        );
      },
      tablecell(token) {
        const tag = token.header ? 'th' : 'td';
        const base = token.header
          ? 'border border-neutral-30 bg-neutral-5 px-3 py-2 font-medium'
          : 'border border-neutral-30 px-3 py-2 align-top';
        const align = CELL_ALIGN_CLASSES[token.align ?? 'left'];
        return `<${tag} class="${base} ${align}">${this.parser.parseInline(token.tokens)}</${tag}>`;
      },
      code({ text }) {
        return `<pre class="my-2 overflow-x-auto rounded bg-neutral-5 p-3 font-mono text-caption"><code>${escapeMarkdownText(text)}</code></pre>\n`;
      },
      codespan({ text }) {
        return `<code class="rounded bg-neutral-5 px-1 font-mono text-caption">${escapeMarkdownText(text)}</code>`;
      },
      link({ href, title, tokens }: Tokens.Link) {
        const text = this.parser.parseInline(tokens);
        const url = readLinkUrl(href);
        if (url === null) {
          return text;
        }
        const titleAttribute = title ? ` title="${escapeHtmlAttribute(title)}"` : '';
        return `<a class="text-link" href="${escapeHtmlAttribute(url)}"${titleAttribute} target="_blank" rel="noopener noreferrer">${text}</a>`;
      },
      // Never load remote images from an AI answer; keep the description only.
      image({ text }) {
        return escapeMarkdownText(text);
      },
    },
  });
}
